import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { isEmpty } from "lodash";
import { ContentType } from "recharts/types/component/DefaultLegendContent";
import { Typography, Box, Paper } from "@mui/material";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useLobby from "@/hooks/use-lobby";

// Interface for props
// interface WelcomeBoxProps {
//   backgroundColor?: string;
//   textColor?: string;
// }

// Define modern chart colors
const COLORS = ["#644980", "#8B5CF6", "#A855F7", "#7C3AED"];

const renderCustomLabel = (entry: any) => {
  return (
    <text
      x={entry.x + 4}
      y={entry.y - 15}
      fill="#1e293b"
      fontSize="16px"
      fontWeight="600"
      textAnchor="middle"
      dominantBaseline="central"
    >
      {entry.value}
    </text>
  );
};

const renderLegend: ContentType = (props) => {
  const { payload } = props;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        mt: 2,
      }}
    >
      {payload?.map((entry, index) => (
        <Box
          key={`item-${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 0.5,
            borderRadius: 1,
            "&:hover": {
              backgroundColor: "#f8fafc",
            },
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: "#475569",
              fontSize: "0.875rem",
            }}
          >
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        elevation={2}
        sx={{
          p: 2,
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          backgroundColor: "white",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            fontWeight: 500,
          }}
        >
          Total: {payload[0].value}
        </Typography>
      </Paper>
    );
  }
  return null;
};

const DoughnutChartComponent: React.FC = () => {
  const { gettenderBStatus } = useLobby();
  const [tenderStatus, setTenderStatus] = useState<
    { status: string; total: number }[]
  >([]);
  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  useEffect(() => {
    const getTotaltenderQ = async () => {
      try {
        const Total = await gettenderBStatus();

        if (!isEmpty(Total)) {
          const formattedData = Total.map(
            (item: { status: string; total: string }) => ({
              status: item.status,
              total: parseInt(item.total, 10),
            })
          );
          setTenderStatus(formattedData);
        } else {
          setTenderStatus([
            { status: t("statistics.finished"), total: 100 },
            { status: t("statistics.unfinished"), total: 0 },
          ]);
        }
      } catch (error) {
        console.error("Error fetching tender status:", error);
      }
    };

    getTotaltenderQ();
  }, []);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            mb: 1,
          }}
        >
          {t("statistics.chartTitle")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            fontSize: "0.875rem",
          }}
        >
          {t("statistics.chartSubtitle")}
        </Typography>
      </Box>

      {/* Chart Container */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip content={<CustomTooltip />} />
            <Pie
              data={tenderStatus}
              dataKey="total"
              nameKey="status"
              cx="50%"
              cy="50%"
              outerRadius="80%"
              innerRadius="60%"
              fill="#e2e8f0"
              label={renderCustomLabel}
              stroke="white"
              strokeWidth={2}
            >
              {tenderStatus.map((_, index) => {
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                );
              })}
            </Pie>
            <Legend
              layout="vertical"
              verticalAlign="middle"
              align="right"
              content={renderLegend}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Summary Stats */}
      {tenderStatus.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: "#f8fafc",
            borderRadius: 2,
            border: "1px solid #e2e8f0",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              mb: 1,
            }}
          >
            Summary
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {tenderStatus.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: COLORS[index % COLORS.length],
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  {item.status}: {item.total}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DoughnutChartComponent;
